import { DecimalString } from "../../api/deposit-refund";
import { numberFormat } from "../../utils/common";
import { getApiErrorMessage, getApiErrorStatus } from "../../utils/api-error.utils";

/** ค่าที่ backend ส่ง null มาแสดงเป็นขีดตามดีไซน์ ไม่ใช่ช่องว่าง */
export const DASH = "–";

/**
 * แปลงจำนวนเงินจาก DECIMAL ของ MySQL (มาเป็น string) ให้เป็น number
 * บาง endpoint คืน number มาเลย (POST /:id/review) จึงรับทั้งสองแบบ
 * คืน null เมื่อไม่มีค่า เพื่อให้แยก "ยังไม่มียอด" ออกจาก "ยอด 0 บาท" ได้
 */
export const toAmount = (value?: DecimalString | null): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
};

/** ดีไซน์แสดงจำนวนเงินแบบไม่มีทศนิยม — numberFormat default เป็น 2 ตำแหน่ง ต้องส่ง 0,0 */
export const amountToView = (value?: DecimalString | null): string => {
  const amount = toAmount(value);
  return amount === null ? DASH : numberFormat(amount, 0, 0);
};

/** เหมือน amountToView แต่ต่อท้าย "บาท" ใช้ใน modal รายละเอียด/หัวข้อ */
export const bahtToView = (value?: DecimalString | null): string => {
  const amount = toAmount(value);
  return amount === null ? DASH : `${numberFormat(amount, 0, 0)} บาท`;
};

/**
 * ยอดหักมี 2 ทศนิยมได้ (base_amount = 529.14) จึงไม่ปัดทิ้งเหมือนยอดรวม
 * แสดง .00 เฉพาะเมื่อมีเศษจริง ไม่งั้นเลขกลมๆ จะรกตา
 */
export const deductToView = (value?: DecimalString | null): string => {
  const amount = toAmount(value);
  if (amount === null) return DASH;
  return Number.isInteger(amount) ? numberFormat(amount, 0, 0) : numberFormat(amount, 2, 2);
};

/**
 * format คอลัมน์ DATE ที่ backend serialize เป็น UTC ISO
 *
 * '2027-07-28T17:00:00.000Z' คือ 29/07/2570 ตามเวลาไทย ถ้าใช้ timezone ของเครื่อง
 * ผู้ใช้ (เช่น server/CI ที่เป็น UTC) จะได้ 28/07/2570 เพี้ยนไป 1 วัน
 * จึงต้อง force Asia/Bangkok — helper กลาง dateToView ไม่รับ timezone
 *
 * รับทั้ง 'YYYY-MM-DD' (log คืนแบบนี้) และ ISO (detail/lease คืนแบบนี้) ได้
 */
export const dateOnlyToView = (value?: string | null): string => {
  if (!value) return DASH;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return DASH;
  return new Intl.DateTimeFormat("th", {
    timeZone: "Asia/Bangkok",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

/**
 * รับข้อความยอดหักจากผู้ใช้ (มี comma ได้) แล้วคืนตัวเลขที่ส่ง backend ได้
 * ค่าว่าง/ติดลบ/ไม่ใช่ตัวเลข = 0 ตามที่ spec ข้อ 3.8 ระบุว่า server ทำ
 */
export const parseDeductInput = (text: string): number => {
  const amount = Number(String(text || "").replace(/,/g, "").trim());
  if (!Number.isFinite(amount) || amount < 0) return 0;
  return amount;
};

/**
 * ปัดเป็น 2 ตำแหน่งด้วยวิธีเดียวกับ backend (spec v2 ข้อ B3)
 * ต้นทาง move_out_penalty_rate / damage_insurance_limit เป็น DOUBLE แต่คอลัมน์ที่บันทึก
 * เป็น DECIMAL(12,2) — BE ปัดก่อน insert ทุกจุด ถ้า front ไม่ปัดด้วยวิธีเดียวกัน
 * ยอดที่โชว์ก่อนกดยืนยันจะไม่เท่ากับยอดในใบคำร้อง
 */
export const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * ข้อความ error ของ module นี้ — 401/403/404 ของโปรเจกต์ตอบ body ที่ errorDescription
 * เป็นสตริงว่าง (GlobalExceptionFilter hardcode ไว้) จึงต้องแปลจาก HTTP status เอง
 * ไม่แก้ filter ตัวกลางเพราะกระทบทุก module
 */
export const getDepositRefundError = (error: any, fallback: string): string => {
  const status = getApiErrorStatus(error);
  if (status === 403) return "ไม่มีสิทธิ์เข้าถึงข้อมูลนี้";
  if (status === 401) return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  if (status === 404) return "ไม่พบข้อมูลที่ต้องการ";
  return getApiErrorMessage(error, fallback);
};

/**
 * คัดตัวอักษรตอนพิมพ์ให้เหลือแค่รูปจำนวนเงิน — ตัวเลข คอมมาคั่นหลักพัน และทศนิยม ≤ 2 ตำแหน่ง
 * ทำที่ onchange เพื่อไม่ให้พิมพ์ตัวอักษร เครื่องหมายลบ หรือจุดซ้อนลงไปได้ตั้งแต่แรก
 * (backend ปัด 2 ตำแหน่งอยู่แล้ว ถ้าปล่อยให้พิมพ์ 3 ตำแหน่งได้ ยอดบนจอจะไม่ตรงกับที่บันทึก)
 */
export const sanitizeMoneyInput = (text: string): string => {
  let cleaned = String(text ?? "").replace(/[^\d.,]/g, "");
  const firstDot = cleaned.indexOf(".");
  if (firstDot >= 0) {
    // เหลือจุดได้จุดเดียว และหลังจุดเป็นตัวเลขไม่เกิน 2 ตัว (ไม่มีคอมมาในทศนิยม)
    const head = cleaned.slice(0, firstDot + 1);
    const tail = cleaned.slice(firstDot + 1).replace(/[.,]/g, "").slice(0, 2);
    cleaned = head + tail;
  }
  return cleaned;
};

/**
 * ข้อความนี้อ่านเป็นจำนวนเงินได้ไหม — '' = ยังไม่กรอก ถือว่าผ่าน (ว่าง = ไม่หัก)
 * ไม่บังคับตำแหน่งคอมมา เพราะ formatMoneyInput จัดรูปให้ตอน blur อยู่แล้ว
 * ถ้าเข้มตอนพิมพ์จะขึ้น error แดงกะพริบระหว่างพิมพ์ '1,23' ซึ่งรบกวนเปล่าๆ
 */
export const isMoneyInput = (text: string): boolean => {
  const raw = String(text ?? "").trim();
  if (!raw) return true;
  const stripped = raw.replace(/,/g, "");
  return /^\d+(\.\d{1,2})?$/.test(stripped) && Number.isFinite(Number(stripped));
};

/** จัดรูปตอน blur ให้มีคอมมาคั่นหลักพัน คงทศนิยมเท่าที่ผู้ใช้พิมพ์ (0 หรือ 2 ตำแหน่ง) */
export const formatMoneyInput = (text: string): string => {
  const raw = String(text ?? "").trim();
  if (!raw) return "";
  const stripped = raw.replace(/,/g, "");
  // อ่านเป็นตัวเลขไม่ได้ ปล่อยข้อความเดิมไว้ให้ validate ฟ้อง ไม่กลืนของที่ผู้ใช้พิมพ์
  if (!/^\d+(\.\d{1,2})?$/.test(stripped)) return raw;
  const amount = Number(stripped);
  if (!Number.isFinite(amount)) return raw;
  const decimals = stripped.includes(".") ? stripped.split(".")[1].length : 0;
  return numberFormat(amount, decimals, decimals);
};
